import { useState, useEffect, useRef, useContext } from 'react';
import Button from "components/molecules/Button";
import { DotButtons } from 'components/molecules/ButtonGroup';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { has } from 'lodash';
import { getAreaState } from 'reduxModules/ducks/container';
import ToolContext from 'components/services/ToolContext';

/**
* @module Carousel
* @description A React component that manages tutorial content display with
* navigation, state tracking, and action triggers for user onboarding.
* 
* @component
* @param {Object} props
* @param {boolean} props.show - Controls carousel visibility
* 
* @description
* Manages tutorial flow by:
* 1. Content Navigation:
*    - Forward/back buttons
*    - Dot navigation
*    - Slide tracking
*    - Progress indicators
* 
* 2. State Management:
*    - Tracks current slide
*    - Syncs UI elements
*    - Handles tutorial completion
*    - Manages modal states
* 
* 3. Action Triggering:
*    - Executes tutorial actions
*    - Updates UI views
*    - Sets spotlight targets
*    - Handles tutorial reset
* 
* @example
* <Carousel 
*   show={true}
* />
* 
* @note
* Features:
* - Bootstrap carousel integration
* - Bi-directional navigation
* - Progress indication
* - Action triggering
* - State persistence
* 
* Tutorial Steps Structure:
* - Image content
* - Text content
* - Associated actions
* - Menu targets
* - Spotlight locations
* 
* Event Handling:
* - Slide transitions
* - Button clicks
* - Tutorial completion
* - Component cleanup
* 
* State Management:
* - Uses Redux for global state
* - Tracks first-time user status
* - Manages modal reopening
* - Persists tutorial progress
* 
* Dependencies:
* - Requires Bootstrap carousel
* - Uses Redux for state
* - Needs tutorial configuration
* - Requires tutorial action handlers
*/
const Carousel = (props) => {
  const tutorialSteps = useSelector(state => state.ui.tutorialConfig);
  const modalReopen = useSelector(state => state.ui.modalReopen);
  const context = useContext(ToolContext);
  const tutorialActions = context.tutorialActions;
  const currentArea = useSelector(state => getAreaState(state));
  const items = Array.from(tutorialSteps, (_, i) => i.toString());
  const [currentSlide, setCurrentSlide] = useState(0);

  const dispatch = useDispatch();

  const moveToSlide = (slide) => {
    setCurrentSlide(slide);
    if (slide >=2) dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false));
    if (has(tutorialSteps[slide], 'action')) {
      const resetValue =  tutorialActions[tutorialSteps[slide].action](currentArea, dispatch);
      if (resetValue) dispatch(ReducersIndex.uiReducers.setTutorialReset(resetValue));
    };
    if (has(tutorialSteps[slide], 'menu')) {
      dispatch(ReducersIndex.uiReducers.setCurrentView(tutorialSteps[slide].menu))
    };
    if (has(tutorialSteps[slide], 'target')) {
      dispatch(ReducersIndex.uiReducers.setSpotlightTarget(tutorialSteps[slide].target))
    };
  };

  const myRef = useRef(null);

  // Reset the carrousel on load
  useEffect(() => {
    if (myRef.current) {
      const children = Array.from(myRef.current.children);
      children.map((child, index) => child.className = `carousel-item ${index === 0 ? 'active' : ''}`)      
      moveToSlide(items[0])
    };
  }, [props.show]);

  // sync the buttons with the arrows
  useEffect(() => {
    const carousel = document.getElementById('tutorial');
    carousel.addEventListener('slide.bs.carousel', (e) => {
      moveToSlide(e.to.toString());
    });

    return () => {
      carousel.removeEventListener('slide.bs.carousel', (e) => {
        moveToSlide(e.to.toString());
      });
    };
  }, []);

  const handleForward = () => {
    if (parseInt(currentSlide) >= tutorialSteps.length - 1) {
      dispatch(ReducersIndex.uiReducers.closeModal('tutorial'));
      if (modalReopen) dispatch(ReducersIndex.uiReducers.openModal(modalReopen));
    };
  };

  return (
    <div id="tutorial" className="carousel slide" data-bs-ride={false} data-bs-wrap={false} >
      <div className='vstack'>
        <div className='hstack'>
          <Button name={'navLeft'} target={'#tutorial'} slide={"prev"} />
          <div ref={myRef} className="carousel-inner mx-5" >
            {tutorialSteps.map((item, index) => (
              <div key={`item-${index}`} className="carousel-item">
                {item.image && <img src={item.image} className="d-block img-fluid p-5" alt="..." />}
                <div className="d-block ">{item.content}</div>
              </div>
            ))}
          </div>
          <Button name={'navRight'} target={'#tutorial'} slide={"next"} onClick={(e) => handleForward()}/>
        </div>
        <DotButtons target={`#tutorial`} items={items} current={currentSlide} />
      </div>
    </div>
  )
};

export default Carousel;
